// כלי הבדיקה של לשונית ה-API (settings?tab=api):
//  - ApiTesterCard: "Postman" מובנה — מריץ בקשה אמיתית מול ה-API הציבורי דרך
//    השרת (אימות/הרשאות/rate-limit אמיתיים) ומציג את התגובה.
//  - ApiInspectorCard: מעקב בקשות חי — כל קריאת API נרשמת (שיטה/נתיב/סטטוס);
//    ב"מצב בדיקה" (חלון 30 דק') נלכד גם התוכן המלא: headers, body ותגובה.
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormField, TextInput, TextArea, FormSelect, StatusBadge, SettingsSection } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { adminApi, type ApiInspectorRequest, type ApiInspectorStatus, type ApiTesterResult } from '@/shared/api/adminApi';

const KEY_STORAGE = 'merkaziya.apiTester.key';

interface Preset {
  label: string;
  method: string;
  path: string;
  query?: string;
  body?: Record<string, unknown> | null;
}

const PRESETS: Preset[] = [
  { label: 'בדיקת חיבור — GET /me', method: 'GET', path: 'me' },
  { label: 'רשימת לידים — GET /leads', method: 'GET', path: 'leads', query: 'per_page=10' },
  {
    label: 'קליטת ליד + הערה — POST /leads', method: 'POST', path: 'leads',
    body: { phone: '0501234567', name: 'ישראל ישראלי', note: 'התעניין במסלול פרימיום', fields: { source_form: 'דף נחיתה' } },
  },
  {
    label: 'ליד לקמפיין + הערת קמפיין — POST /leads', method: 'POST', path: 'leads',
    body: { phone: '0501234567', campaign: 'שם הקמפיין', campaign_note: 'לחזור אחרי 17:00', campaign_fields: { budget: '5000' } },
  },
  {
    label: 'הערה לליד קיים (בלי הליד) — POST /leads/{id}/notes', method: 'POST', path: 'leads/0501234567/notes',
    body: { body: 'עדכון מהמערכת החיצונית' },
  },
  { label: 'הערות של ליד — GET /leads/{id}/notes', method: 'GET', path: 'leads/0501234567/notes' },
  {
    label: 'עדכון ליד — PATCH /leads/{id}', method: 'PATCH', path: 'leads/0501234567',
    body: { status: 'in_progress', note: 'שודרג לחבילה 2' },
  },
  { label: 'שיחות אחרונות — GET /calls', method: 'GET', path: 'calls', query: 'per_page=10' },
  { label: 'אנשי קשר — GET /contacts', method: 'GET', path: 'contacts' },
  { label: 'חיוג יוצא — POST /calls/originate', method: 'POST', path: 'calls/originate', body: { extension: '4001', to: '0501234567' } },
];

function parseQuery(q: string): Record<string, string> | null {
  const t = q.trim();
  if (!t) return null;
  const out: Record<string, string> = {};
  new URLSearchParams(t).forEach((v, k) => { out[k] = v; });
  return Object.keys(out).length ? out : null;
}

function JsonBlock({ value, maxHeight = 260 }: { value: unknown; maxHeight?: number }) {
  return (
    <pre
      dir="ltr"
      style={{
        margin: 0, padding: 10, background: '#0f172a', color: '#e2e8f0',
        borderRadius: 'var(--r-2)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)',
        lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'auto', maxHeight,
      }}
    >
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function statusTone(status: number): 'ok' | 'warn' | 'danger' | 'info' {
  if (status >= 200 && status < 300) return 'ok';
  if (status >= 400 && status < 500) return 'warn';
  if (status >= 500) return 'danger';
  return 'info';
}

// ── קונסולת בדיקות ──────────────────────────────────────────────────────────
export function ApiTesterCard() {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) ?? '');
  const [preset, setPreset] = useState('0');
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('me');
  const [query, setQuery] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ApiTesterResult | null>(null);

  const applyPreset = (idx: string) => {
    setPreset(idx);
    const p = PRESETS[Number(idx)];
    if (!p) return;
    setMethod(p.method);
    setPath(p.path);
    setQuery(p.query ?? '');
    setBodyText(p.body ? JSON.stringify(p.body, null, 2) : '');
  };

  async function run() {
    if (!apiKey.trim()) { toast('הדבק מפתח API (נוצר למעלה בעמוד)', 'danger'); return; }
    let body: unknown | null = null;
    if (bodyText.trim()) {
      try { body = JSON.parse(bodyText); } catch { toast('ה-Body אינו JSON תקין', 'danger'); return; }
    }
    sessionStorage.setItem(KEY_STORAGE, apiKey.trim());
    setRunning(true);
    setResult(null);
    try {
      const r = await adminApi.apiTesterRun({
        method, path: path.trim(), api_key: apiKey.trim(), query: parseQuery(query), body,
      });
      setResult(r);
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בהרצת הבקשה', 'danger');
    } finally {
      setRunning(false);
    }
  }

  return (
    <SettingsSection
      icon="bolt"
      title="קונסולת בדיקות"
      description="הרץ בקשה אמיתית מול ה-API — כולל אימות, הרשאות ומגבלות קצב — וראה את התגובה כפי שמערכת חיצונית תקבל אותה."
      selfSaving
    >
      <div className="stack" style={{ gap: 10 }}>
        <FormField label="תרחיש מוכן">
          <FormSelect value={preset} onChange={(e) => applyPreset(e.target.value)}>
            {PRESETS.map((p, i) => <option key={p.label} value={String(i)}>{p.label}</option>)}
          </FormSelect>
        </FormField>

        <FormField label="מפתח API" hint="נשמר רק בדפדפן לזמן החיבור הנוכחי.">
          <TextInput type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} dir="ltr" placeholder="הדבק מפתח שנוצר למעלה" />
        </FormField>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 110px) 1fr', gap: 8 }}>
          <FormField label="שיטה">
            <FormSelect value={method} onChange={(e) => setMethod(e.target.value)}>
              {['GET', 'POST', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
            </FormSelect>
          </FormField>
          <FormField label="נתיב (אחרי /v1/)">
            <TextInput value={path} onChange={(e) => setPath(e.target.value)} dir="ltr" placeholder="leads" />
          </FormField>
        </div>

        <FormField label="פרמטרים (query)" hint="פורמט key=value&key2=value2 — אופציונלי.">
          <TextInput value={query} onChange={(e) => setQuery(e.target.value)} dir="ltr" placeholder="per_page=10&status=new" />
        </FormField>

        {method !== 'GET' && (
          <FormField label="Body (JSON)">
            <TextArea
              dir="ltr"
              rows={7}
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', lineHeight: 1.5 }}
              spellCheck={false}
            />
          </FormField>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn primary" onClick={() => void run()} disabled={running}>
            {running ? 'שולח…' : 'שלח בקשה'}
          </button>
          {result && (
            <>
              <StatusBadge tone={statusTone(result.status)}>{result.status}</StatusBadge>
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{result.duration_ms}ms</span>
              <code dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontFamily: 'var(--font-mono)' }}>{method} /{path}</code>
            </>
          )}
        </div>

        {result && <JsonBlock value={result.body} maxHeight={320} />}
      </div>
    </SettingsSection>
  );
}

// ── מעקב בקשות חי (Inspector) ───────────────────────────────────────────────
function fmtCountdown(untilIso: string | null): string {
  if (!untilIso) return '';
  const ms = new Date(untilIso).getTime() - Date.now();
  if (ms <= 0) return '0:00';
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function ApiInspectorCard() {
  const [status, setStatus] = useState<ApiInspectorStatus | null>(null);
  const [rows, setRows] = useState<ApiInspectorRequest[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [, forceTick] = useState(0);
  const lastId = useRef(0);

  const loadStatus = useCallback(() => {
    adminApi.apiInspectorStatus().then(setStatus).catch(() => {});
  }, []);

  const poll = useCallback(() => {
    adminApi.apiInspectorRequests(lastId.current || undefined)
      .then((fresh) => {
        if (!fresh.length) return;
        setRows((cur) => {
          const seen = new Set(cur.map((r) => r.id));
          const merged = [...fresh.filter((r) => !seen.has(r.id)), ...cur]
            .sort((a, b) => b.id - a.id)
            .slice(0, 50);
          lastId.current = merged[0]?.id ?? lastId.current;
          return merged;
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadStatus();
    poll();
    const t = setInterval(poll, 2500);
    return () => clearInterval(t);
  }, [loadStatus, poll]);

  // שעון לספירה-לאחור של חלון מצב הבדיקה.
  const capturing = !!status?.capturing && !!status.capture_until && new Date(status.capture_until).getTime() > Date.now();
  useEffect(() => {
    if (!capturing) return;
    const t = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [capturing]);

  async function toggle() {
    try {
      const next = await adminApi.apiInspectorToggle(!capturing);
      setStatus(next);
      if (!next.capturing) {
        // כיבוי מוחק את התוכן שנלכד בשרת — נשקף גם בתצוגה.
        setRows((cur) => cur.map((r) => ({ ...r, captured: false, headers: null, query: null, body: null, response_body: null })));
        toast('מצב בדיקה כובה — התוכן שנלכד נמחק', 'ok');
      } else {
        toast(`מצב בדיקה פעיל ל-${next.window_minutes} דקות`, 'ok');
      }
    } catch {
      toast('שגיאה בהחלפת מצב', 'danger');
    }
  }

  async function clear() {
    try {
      await adminApi.apiInspectorClear();
      setRows((cur) => cur.map((r) => ({ ...r, captured: false, headers: null, query: null, body: null, response_body: null })));
      toast('נוקה', 'ok');
    } catch {
      toast('שגיאה בניקוי', 'danger');
    }
  }

  return (
    <SettingsSection
      icon="eye"
      title="מעקב בקשות חי"
      description="כל בקשה למפתחות ה-API נרשמת כאן (שיטה, נתיב, סטטוס). הפעל מצב בדיקה כדי ללכוד גם את התוכן המלא — כותרות, גוף ותגובה — למשך 30 דקות; בכיבוי התוכן נמחק."
      selfSaving
      action={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {capturing && (
            <StatusBadge tone="warn">
              נותרו {fmtCountdown(status?.capture_until ?? null)}
            </StatusBadge>
          )}
          <button type="button" className="btn ghost" onClick={() => void clear()} title="ניקוי תוכן שנלכד">
            <Icon name="trash" size={14} />
          </button>
          <button type="button" className={capturing ? 'btn danger' : 'btn primary'} onClick={() => void toggle()}>
            {capturing ? 'כבה מצב בדיקה' : 'הפעל מצב בדיקה'}
          </button>
        </div>
      }
    >
      {rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
          עדיין אין בקשות. שלח בקשה ל-API (או השתמש בקונסולת הבדיקות למעלה) והיא תופיע כאן תוך שניות.
        </p>
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          {rows.map((r) => {
            const open = openId === r.id;
            return (
              <div key={r.id} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden', background: 'var(--surface)' }}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : r.id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'start' }}
                >
                  <StatusBadge tone={statusTone(r.status)} size="sm">{r.status}</StatusBadge>
                  <code dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.method} {r.path.replace('/api/v1/merkaziya', '')}
                  </code>
                  {r.captured && <StatusBadge tone="info" size="sm">נלכד</StatusBadge>}
                  {r.error && <StatusBadge tone="danger" size="sm">{r.error}</StatusBadge>}
                  <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', flexShrink: 0 }}>
                    {r.duration_ms != null ? `${r.duration_ms}ms · ` : ''}
                    {r.created_at ? new Date(r.created_at).toLocaleTimeString('he-IL') : ''}
                  </span>
                  <Icon name={open ? 'chevronD' : 'chevron'} size={13} color="var(--muted)" />
                </button>
                {open && (
                  <div className="stack" style={{ gap: 8, padding: '0 10px 10px' }}>
                    {!r.captured ? (
                      <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                        נרשמו רק נתוני הבסיס. הפעל מצב בדיקה כדי ללכוד כותרות, גוף ותגובה בבקשות הבאות.
                      </p>
                    ) : (
                      <>
                        {r.query && Object.keys(r.query).length > 0 && (
                          <div><strong style={{ fontSize: 'var(--t-xs)' }}>Query</strong><JsonBlock value={r.query} maxHeight={120} /></div>
                        )}
                        {r.body && (
                          <div><strong style={{ fontSize: 'var(--t-xs)' }}>Body</strong><JsonBlock value={r.body} maxHeight={200} /></div>
                        )}
                        {r.headers && (
                          <div><strong style={{ fontSize: 'var(--t-xs)' }}>Headers</strong><JsonBlock value={r.headers} maxHeight={160} /></div>
                        )}
                        {r.response_body && (
                          <div><strong style={{ fontSize: 'var(--t-xs)' }}>תגובה</strong><JsonBlock value={r.response_body} maxHeight={200} /></div>
                        )}
                      </>
                    )}
                    {r.ip && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }} dir="ltr">IP: {r.ip}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SettingsSection>
  );
}
